import React, { useState, useEffect } from 'react';
import { Footer } from './Footer';

const SmsOptInPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    honeypot: '',
  });
  const [transactionalConsent, setTransactionalConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [consentError, setConsentError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitStatus, setSubmitStatus] = useState('');
  const [formStartTime] = useState(Date.now());

  // Load reCAPTCHA v3
  useEffect(() => {
    // Skip reCAPTCHA injection during react-snap / headless pre-rendering
    if (navigator.webdriver || window.location.hostname === 'localhost') return;
    const script = document.createElement('script');
    script.src = "https://www.google.com/recaptcha/api.js?render=6Ld5QC0tAAAAACBa4DGyEe6SVLaGCVB8huTeuxFF";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.grecaptcha.ready(() => {
        console.log('reCAPTCHA v3 loaded for SMS opt-in');
      });
    };
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [showPhoneHint, setShowPhoneHint] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      // Only allow + as first character, then digits only
      const filtered = value.replace(/[^+\d]/g, '');
      const sanitized = filtered.charAt(0) === '+'
        ? '+' + filtered.slice(1).replace(/\+/g, '')
        : filtered.replace(/\+/g, '');
      // Block input if first character is not +
      if (sanitized.length > 0 && sanitized.charAt(0) !== '+') {
        setShowPhoneHint(true);
        setTimeout(() => setShowPhoneHint(false), 2500);
        return;
      }
      if (sanitized !== value) {
        setShowPhoneHint(true);
        setTimeout(() => setShowPhoneHint(false), 2500);
      }
      setFormData(prev => ({ ...prev, phone: sanitized }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (submitMessage) {
      setSubmitMessage('');
      setSubmitStatus('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Phone format validation
    const trimmedPhone = formData.phone.trim();
    if (trimmedPhone && !trimmedPhone.startsWith('+')) {
      setShowPhoneModal(true);
      return;
    }

    // At least one consent checkbox must be checked
    if (!transactionalConsent && !marketingConsent) {
      setConsentError('Please select at least one consent option to proceed.');
      return;
    }
    setConsentError('');

    setIsSubmitting(true);
    setSubmitMessage('');
    setSubmitStatus('');

    try {
      // Generate reCAPTCHA token
      if (!window.grecaptcha) {
        throw new Error('reCAPTCHA not loaded');
      }
      const recaptchaToken = await new Promise((resolve, reject) => {
        window.grecaptcha.ready(async () => {
          try {
            const token = await window.grecaptcha.execute('6Ld5QC0tAAAAACBa4DGyEe6SVLaGCVB8huTeuxFF', {action: 'consent'});
            resolve(token);
          } catch (error) {
            reject(error);
          }
        });
      });

      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: trimmedPhone,
        transactionalConsent,
        marketingConsent,
        recaptchaToken,
        formStartTime,
        honeypot: formData.honeypot,
      };

      const response = await fetch('/api/consent-proxy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        setSubmitMessage('Thank you! Your information has been recorded.');
        setFormData({ name: '', email: '', phone: '', honeypot: '' });
        setTransactionalConsent(false);
        setMarketingConsent(false);
      } else {
        throw new Error(result.message || 'Submission failed');
      }
    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage('Sorry, there was an error. Please try again or contact us directly on +64 9 886 4638.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
    <div className="telematics-page">
      {/* Logo Header */}
      <div className="telematics-header">
        <div className="container">
          <div className="header-content">
            <div className="logo-container">
              <a href="/">
                <img
                  src="/img/Banner_16x_TR_Trim&ColorEdit.png"
                  alt="Yuccan Technologies Logo"
                  className="telematics-logo"
                />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Contact / Opt-In Section */}
      <section id="contact" className="text-center">
        <div className="container">
          <div className="section-title">
            <h2>Yuccan International Limited — A2P Call / SMS Opt-In</h2>
          </div>
          <div className="row">
            <div className="col-md-8 col-md-offset-2">
                <p style={{fontSize: '16px', color: '#fff', marginBottom: '25px', textAlign: 'justify'}}>By providing your phone number below, you may opt in to receive text messages from Yuccan International Limited, doing business as Yuccan Technologies. Please select at least one consent option to indicate the types of messages you agree to receive.</p>
                <form onSubmit={handleSubmit} className="telematics-contact-form">
                  {/* Honeypot field - hidden from users, bots will fill it */}
                  <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}>
                    <input
                      type="text"
                      name="honeypot"
                      value={formData.honeypot}
                      onChange={handleInputChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>
                  <div className="form-group">
                    <input
                      type="text"
                      name="name"
                      className="form-control"
                      placeholder="Your Name *"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <input
                          type="email"
                          name="email"
                          className="form-control"
                          placeholder="Email Address *"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group" style={{position: 'relative'}}>
                        <input
                          type="tel"
                          name="phone"
                          className="form-control"
                          placeholder="Phone Number (optional)"
                          value={formData.phone}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                        />
                        {showPhoneHint && (
                          <div style={{
                            position: 'absolute', top: '-38px', left: '0', right: '0',
                            backgroundColor: '#333', color: '#fff', fontSize: '12px',
                            padding: '6px 10px', borderRadius: '4px', textAlign: 'center',
                            zIndex: 10, whiteSpace: 'nowrap'
                          }}>
                            Only + followed by digits allowed
                            <div style={{
                              position: 'absolute', bottom: '-6px', left: '50%', marginLeft: '-6px',
                              width: 0, height: 0, borderLeft: '6px solid transparent',
                              borderRight: '6px solid transparent', borderTop: '6px solid #333'
                            }} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Transactional Consent Checkbox */}
                  <div style={{clear: 'both', overflow: 'hidden', marginTop: '5px', marginBottom: '10px'}}>
                    <label style={{display: 'flex', alignItems: 'flex-start', cursor: 'pointer', fontSize: '13px', color: '#fff', fontWeight: 'normal', float: 'none', width: '100%'}}>
                      <input
                        type="checkbox"
                        checked={transactionalConsent}
                        onChange={(e) => { setTransactionalConsent(e.target.checked); setConsentError(''); }}
                        disabled={isSubmitting}
                        style={{marginRight: '8px', marginTop: '2px', flexShrink: 0}}
                      />
                      <span style={{textAlign: 'justify'}}>
                        By checking this box, I consent to receive non-marketing text messages from Yuccan Technologies at the phone number provided. Messages may include responses to inquiries, appointment or consultation confirmations, service updates, and follow-up communications relevant to your request. Message frequency is twice or less per day; message and data rates may apply. Reply STOP to opt out.
                      </span>
                    </label>
                  </div>

                  {/* Marketing Consent Checkbox */}
                  <div style={{clear: 'both', overflow: 'hidden', marginBottom: '15px'}}>
                    <label style={{display: 'flex', alignItems: 'flex-start', cursor: 'pointer', fontSize: '13px', color: '#fff', fontWeight: 'normal', float: 'none', width: '100%'}}>
                      <input
                        type="checkbox"
                        checked={marketingConsent}
                        onChange={(e) => { setMarketingConsent(e.target.checked); setConsentError(''); }}
                        disabled={isSubmitting}
                        style={{marginRight: '8px', marginTop: '2px', flexShrink: 0}}
                      />
                      <span style={{textAlign: 'justify'}}>
                        By checking this box, I consent to receive marketing and promotional text messages from Yuccan Technologies at the phone number provided. Messages may include special offers, discounts, and new product updates among others. Message frequency is twice or less per day; message and data rates may apply. Reply STOP to opt out.
                      </span>
                    </label>
                  </div>

                  {consentError && (
                    <div className="alert alert-danger" style={{marginBottom: '15px'}}>
                      {consentError}
                    </div>
                  )}

                  {submitMessage && (
                    <div className={`alert ${submitStatus === 'success' ? 'alert-success' : 'alert-danger'}`}>
                      {submitMessage}
                    </div>
                  )}

                  <div className="privacy-notice" style={{margin: '15px 0', fontSize: '14px', color: '#fff'}}>
                    <p style={{fontSize: '16px', margin: 0}}>
                      For more information, read our{" "}
                      <a href="/legal/privacy-policy.html" target="_blank" rel="noopener noreferrer" style={{color: '#2B4B8C'}}>
                        Privacy Policy
                      </a>
                      {" "}and{" "}
                      <a href="/legal/terms-of-use.html" target="_blank" rel="noopener noreferrer" style={{color: '#2B4B8C'}}>
                        Terms of Use
                      </a>
                      .
                    </p>
                  </div>

                  <div className="text-center">
                    <button
                      type="submit"
                      className="btn btn-custom btn-lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit'}
                    </button>
                  </div>
                </form>
            </div>
          </div>
        </div>
      </section>

      {/* Phone Format Modal */}
      {showPhoneModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '8px', padding: '30px',
            maxWidth: '400px', width: '90%', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <h4 style={{ marginBottom: '15px', color: '#333' }}>Phone Format Required</h4>
            <p style={{ fontSize: '15px', color: '#555', marginBottom: '20px' }}>
              International format (<strong>+...</strong>) required.<br />
              Example: +1 555 123 4567
            </p>
            <button
              className="btn btn-custom"
              onClick={() => setShowPhoneModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
    </div>
  );
};

export default SmsOptInPage;
